(() => {
  class CustomSliderCarousel extends HTMLElement {
    constructor() {
      super();
      this.boundHandleResize = this.#handleResize.bind(this);
      this.boundHandleKeydown = this.#handleKeydown.bind(this);
      this.resizeRaf = null;
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this.track = this.querySelector('.custom-slider__track');
      this.prevBtn = this.querySelector('.custom-slider__nav--prev');
      this.nextBtn = this.querySelector('.custom-slider__nav--next');
      this.pagination = this.querySelector('.custom-slider__pagination');

      if (!this.track || !this.prevBtn || !this.nextBtn) return;

      this.slidesData = this.#parseSlidesData();
      if (!this.slidesData.length) return;

      this.#renderSlides();
      this.slides = Array.from(this.track.children);
      this.#renderPagination();

      this.currentIndex = Math.min(this.CLONES_PER_SIDE, this.slides.length - 1);
      this.isAnimating = false;
      this.isTransitioning = false;
      this.baseCardWidth = this.#getBaseCardWidth();
      this.#updateCardInfoOffsets();
      this.#positionSlides(false);

      this.prevBtn.addEventListener('click', () => this.#prevSlide());
      this.nextBtn.addEventListener('click', () => this.#nextSlide());
      this.slides.forEach((slide, index) => {
        slide.addEventListener('click', () => {
          if (this.isAnimating || this.isTransitioning) return;
          const url = slide.dataset.url;
          const diff = index - this.currentIndex;
          if (diff === 0 && url) {
            window.location.href = url;
            return;
          }
          if (Math.abs(diff) <= 2) this.#goToIndex(index);
        });
      });

      this.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
      });

      this.addEventListener('touchend', (e) => {
        if (this.isAnimating || this.isTransitioning) return;
        const touchEndX = e.changedTouches[0].screenX;
        const diff = this.touchStartX - touchEndX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) this.#nextSlide();
          else this.#prevSlide();
        }
      });

      window.addEventListener('resize', this.boundHandleResize);
      document.addEventListener('keydown', this.boundHandleKeydown);

      this.#playIntroAnimation();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundHandleResize);
      document.removeEventListener('keydown', this.boundHandleKeydown);
    }

    get CLONES_PER_SIDE() {
      return 3;
    }

    get CONFIG() {
      return {
        cardOverlap: 48,
        arcHeight: 16,
        scaleStep: 0.2,
        opacityStep: 0.12,
        visibleCards: 7,
      };
    }

  get TRANSITION_DURATION() {
    return 420;
  }

    #parseSlidesData() {
      const raw = this.dataset.slides || '[]';
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }

    #renderSlides() {
      const originals = this.slidesData;
      const total = originals.length;
      const allSlides = [];

      const createSlide = (card, index) => {
        const slide = document.createElement('div');
        slide.className = 'custom-slider__slide';
        slide.dataset.index = index;
        slide.dataset.type = card.type ?? (index % 6);
        if (card.url) {
          slide.dataset.url = card.url;
        }
        if (card.titleColor) {
          slide.style.setProperty('--card-title-color', card.titleColor);
        }
        if (card.image) {
          slide.style.backgroundImage = `url(${card.image})`;
        }
        slide.innerHTML = `
          <div class="custom-slider__card-info">
            <h3 class="custom-slider__card-title">${card.title || ''}</h3>
            <p class="custom-slider__card-subtitle">${card.subtitle || ''}</p>
            <p class="custom-slider__card-description">${(card.description || '').replace(/\n/g, '<br>')}</p>
          </div>
        `;
        return slide;
      };

      for (let i = this.CLONES_PER_SIDE; i > 0; i--) {
        const originalIndex = total - i;
        allSlides.push(createSlide(originals[originalIndex], allSlides.length));
      }

      originals.forEach((card) => {
        allSlides.push(createSlide(card, allSlides.length));
      });

      for (let i = 0; i < this.CLONES_PER_SIDE; i++) {
        allSlides.push(createSlide(originals[i], allSlides.length));
      }

      this.track.replaceChildren(...allSlides);
    }

    #getBaseCardWidth() {
      const firstSlide = this.track.querySelector('.custom-slider__slide');
      if (!firstSlide) return 400;
      const width = parseFloat(getComputedStyle(firstSlide).width);
      return Number.isFinite(width) ? width : 400;
    }

    #updateCardInfoOffsets() {
      const infos = this.track.querySelectorAll('.custom-slider__card-info');
      infos.forEach((info) => {
        const height = info.offsetHeight || info.clientHeight || info.getBoundingClientRect().height || 0;
        const bottom = -(height + 8);
        info.style.bottom = `${bottom}px`;
      });
    }

    #getScaleForDiff(absDiff) {
      return Math.max(0.65, 1 - absDiff * this.CONFIG.scaleStep);
    }

    #getOffsetForDiff(diff) {
      if (diff === 0) return 0;

      const direction = diff > 0 ? 1 : -1;
      let offset = 0;
      let prevWidth = this.baseCardWidth * this.#getScaleForDiff(0);

      for (let step = direction; direction > 0 ? step <= diff : step >= diff; step += direction) {
        const absStep = Math.abs(step);
        const currentWidth = this.baseCardWidth * this.#getScaleForDiff(absStep);
        const distanceBetweenCenters = (prevWidth + currentWidth) / 2 - this.CONFIG.cardOverlap;

        offset += distanceBetweenCenters * direction;
        prevWidth = currentWidth;
      }

      return offset;
    }

    #handleResize() {
      this.baseCardWidth = this.#getBaseCardWidth();
      this.#updateCardInfoOffsets();
      this.#positionSlides(false);

      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.#updateCardInfoOffsets();
        this.#positionSlides(false);
        this.resizeRaf = null;
      });
    }

    #handleKeydown(event) {
      if (this.isAnimating || this.isTransitioning) return;
      if (event.key === 'ArrowLeft') this.#prevSlide();
      if (event.key === 'ArrowRight') this.#nextSlide();
    }

    #positionSlides(animate = true) {
    const hideThreshold = Math.floor(this.CONFIG.visibleCards / 2) + 1;

    this.slides.forEach((slide, index) => {
        if (animate) slide.classList.add('animating');
        else slide.classList.remove('animating');

        const diff = index - this.currentIndex;
        const absDiff = Math.abs(diff);

      if (slide._revealTimeout) {
        clearTimeout(slide._revealTimeout);
        slide._revealTimeout = null;
      }

      if (absDiff > hideThreshold) {
          slide.style.opacity = '0';
          slide.style.pointerEvents = 'none';
          slide.style.zIndex = '0';
        slide.dataset.hiddenFromCarousel = 'true';
          return;
        }

      const wasHidden = slide.dataset.hiddenFromCarousel === 'true';
      slide.dataset.hiddenFromCarousel = 'false';

        const xOffset = this.#getOffsetForDiff(diff);
        const yOffset = Math.pow(absDiff, 1.5) * this.CONFIG.arcHeight;
        const scale = this.#getScaleForDiff(absDiff);
        const opacity = Math.max(0.5, 1);
        const baseZ = 1;
        const zIndex = Math.max(1, Math.round(baseZ - diff * -1 + (diff > 0 ? 1 : 0)));

        slide.style.transform = `translateX(${xOffset}px) translateY(${yOffset}px) scale(${scale})`;
      slide.style.zIndex = zIndex;

      const reveal = () => {
        slide.style.opacity = opacity;
        slide.style.pointerEvents = 'auto';
        slide._revealTimeout = null;
      };

      if (wasHidden && animate) {
        slide.style.opacity = '0';
        slide.style.pointerEvents = 'none';

        slide._revealTimeout = setTimeout(reveal, this.TRANSITION_DURATION);
      } else {
        reveal();
      }

        slide.classList.toggle('active', diff === 0);
      });

      this.#updatePagination();
    }

    #checkBounds() {
      const minReal = this.CLONES_PER_SIDE;
      const maxReal = this.CLONES_PER_SIDE + this.slidesData.length - 1;

      if (this.currentIndex < minReal) {
        this.currentIndex += this.slidesData.length;
        this.#positionSlides(false);
      } else if (this.currentIndex > maxReal) {
        this.currentIndex -= this.slidesData.length;
        this.#positionSlides(false);
      }
    }

    #goToIndex(index, { animate = true, skipBounds = false } = {}) {
      if (this.isTransitioning) return;
      this.currentIndex = index;
      this.isTransitioning = true;

      this.#positionSlides(animate);

      setTimeout(() => {
        if (!skipBounds) {
          this.#checkBounds();
        }
        this.isTransitioning = false;
        this.#updatePagination();
      }, animate ? this.TRANSITION_DURATION : 0);
    }

    #nextSlide() {
      if (this.isTransitioning || this.isAnimating) return;
      this.#goToIndex(this.currentIndex + 1);
    }

    #prevSlide() {
      if (this.isTransitioning || this.isAnimating) return;
      this.#goToIndex(this.currentIndex - 1);
    }

    #playIntroAnimation() {
      if (this.introPlayed) return;
      this.introPlayed = true;

      // Place slides instantly with animations off
      this.slides.forEach((slide) => slide.classList.remove('animating'));
      this.#positionSlides(false);

      // Force layout so initial positions render without movement
      this.track.getBoundingClientRect();

      // Start animated loop on next frame to avoid visible fan-out
      requestAnimationFrame(() => {
        this.slides.forEach((slide) => slide.classList.add('animating'));

        // Run a full loop through all real slides once without bounds jumps
        this.isAnimating = true;
        const steps = this.slidesData.length;
        let completed = 0;

        const tick = () => {
          if (this.isTransitioning) {
            setTimeout(tick, 32);
            return;
          }

          if (completed >= steps) {
            this.isAnimating = false;
            this.#checkBounds();
            this.#positionSlides(false);
            this.#updatePagination();
            return;
          }

          this.#goToIndex(this.currentIndex + 1, { animate: true, skipBounds: true });
          completed += 1;

          setTimeout(tick, this.TRANSITION_DURATION);
        };

        setTimeout(tick, 80);
      });
    }

    #renderPagination() {
      if (!this.pagination) return;

      this.pagination.innerHTML = '';
      this.paginationDots = this.slidesData.map((_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'custom-slider__pagination-button';
        button.setAttribute('aria-label', `Slide ${index + 1}`);
        button.setAttribute('role', 'tab');
        button.addEventListener('click', () => this.#goToRealIndex(index));
        this.pagination.appendChild(button);
        return button;
      });

      this.#updatePagination();
    }

    #getRealIndex() {
      if (!this.slidesData.length) return 0;

      const normalized = Math.round(this.currentIndex);
      const raw = (normalized - this.CLONES_PER_SIDE) % this.slidesData.length;
      return (raw + this.slidesData.length) % this.slidesData.length;
    }

    #goToRealIndex(realIndex) {
      const targetIndex = this.CLONES_PER_SIDE + realIndex;
      this.#goToIndex(targetIndex);
    }

    #updatePagination() {
      if (!this.paginationDots || !this.paginationDots.length) return;

      const realIndex = this.#getRealIndex();
      this.paginationDots.forEach((dot, index) => {
        const isActive = index === realIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }
  }

  customElements.define('custom-slider-carousel', CustomSliderCarousel);
})();

